/**
 * Plays a notification sound when a new assistant message starts streaming.
 * Watches Redux state for newly added assistant messages with isStreaming=true.
 */

import {
  selectMessageEntities,
  selectMessageIdsByConversation,
} from '@saaga/shared/store/slices/chat-selectors.store'
import { useAppSelector } from '@saaga/shared/store/store'
import { useEffect, useRef } from 'react'

import { playMessageSound } from '@/utils/notification-sound.util'

const EMPTY_ID_LIST: string[] = []

export const useIncomingMessageSound = (conversationId: string | null): void => {
  const seenIdSetRef = useRef(new Set<string>())

  const idsByConversation = useAppSelector(selectMessageIdsByConversation)
  const messageIdList = conversationId
    ? (idsByConversation[conversationId] ?? EMPTY_ID_LIST)
    : EMPTY_ID_LIST

  const messageEntities = useAppSelector(selectMessageEntities)
  const entitiesRef = useRef(messageEntities)
  entitiesRef.current = messageEntities

  useEffect(() => {
    seenIdSetRef.current = new Set<string>()
  }, [conversationId])

  useEffect(() => {
    for (const id of messageIdList) {
      if (seenIdSetRef.current.has(id)) continue
      seenIdSetRef.current.add(id)

      const entity = entitiesRef.current[id]
      if (!entity) continue
      if (entity.type !== 'message' && entity.type !== 'tool-call') continue
      if (entity.sender === 'assistant' && entity.isStreaming) {
        playMessageSound()
        return
      }
    }
  }, [messageIdList])
}
